import React, { FC, useContext, useState } from 'react';
import { faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GoToRoomDetailsParams,
  GroupedRoomClass,
  Hotel,
  HotelRoom,
  RoomClass,
  RoomVariantMeta,
} from 'ts/hotel/interfaces/HotelResponse';
import { MapHotelContext } from 'contexts/MapHotelContext';
import CancellationPolicyModal from '../Modals/CancellationPolicyModal';
import { FilterRoom } from 'ts/hotel/classes/FilterRoom';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import RoomIcons from './RoomIcons';
import { formatPriceHotel } from 'utils/hotel';
import { useHotelProposal } from 'hooks/useProposal';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import { MainContext } from 'contexts/MainContext';

interface HotelCardRooms2Props {
  rooms: RoomClass[];
  hotel: Hotel;
  goToDetails: (params: GoToRoomDetailsParams) => void;
  showRoomsInTab?: boolean;
  onOpenRooms?: (rcKey: string, e?: React.MouseEvent<HTMLDivElement>) => void;
  roomsToPaint?: number;
  byCategoryMode?: boolean;
  isSingle?: boolean;
}

interface CheapestOffer {
  type: 'red' | 'green';
  meta: RoomVariantMeta;
  room: RoomClass;
}

const isRedCxl = (room: RoomClass) => {
  const limitDate = new Date(room.cxl.limitDate);
  return limitDate < new Date() && limitDate > new Date(2000, 1, 1);
};

const getSupplierValue = (room: RoomClass) => {
  return room.suppDesc || room.suppText || room.suppCode || '';
};

const toVariantMeta = (room: RoomClass): RoomVariantMeta => ({
  cxl: room.cxl,
  board: room.board,
  remarks: room.remarks,
  remCondition: room.remCondition,
  price: room.price,
  rcUniqueKey: room.rcUniqueKey,
  suppDesc: getSupplierValue(room),
});

const toGroupedRoom = (groupRooms: RoomClass[]): GroupedRoomClass => {
  const sorted = [...groupRooms].sort((a, b) => a.price.usd - b.price.usd);
  return {
    ...sorted[0],
    suppliers: Array.from(new Set(groupRooms.map(getSupplierValue).filter(Boolean))),
    redData: groupRooms.filter(isRedCxl).sort((a, b) => a.price.usd - b.price.usd).map(toVariantMeta),
    greenPrices: groupRooms
      .filter((room) => !isRedCxl(room))
      .sort((a, b) => a.price.usd - b.price.usd)
      .map(toVariantMeta),
  };
};

const groupRoomListsByKey = (
  rooms: RoomClass[],
  getKey: (room: RoomClass) => string
): RoomClass[][] => {
  const groups = new Map<string, RoomClass[]>();
  rooms.forEach((room, index) => {
    const key = getKey(room) || room.rcUniqueKey || `${index}`;
    const existing = groups.get(key) || [];
    existing.push(room);
    groups.set(key, existing);
  });
  return Array.from(groups.values());
};

const groupRoomListsByKeys = (
  rooms: RoomClass[],
  getKeys: ((room: RoomClass) => string)[]
): RoomClass[][] => {
  return getKeys.reduce(
    (groups, getKey) => groups.flatMap((groupRooms) => groupRoomListsByKey(groupRooms, getKey)),
    [rooms] as RoomClass[][]
  );
};

const getRoomValue = (value: string | undefined) => (typeof value === 'string' ? value.trim() : '');

const getBoardKey = (room: RoomClass) => room.board?.basis?.code || '';

const getRoomDisplayKey = (room: RoomClass) => {
  const hotelRoom = room.hotelRooms[0];
  const displayName = (hotelRoom?.displayName || '').replace(/<[^>]*>/g, '').trim();

  return getRoomValue(hotelRoom?.displayNameCleared) || displayName;
};

const getCategoryKey = (room: RoomClass) => {
  return getRoomValue(room.hotelRooms[0]?.cat) || getRoomDisplayKey(room);
};

const getTypeKey = (room: RoomClass) => getRoomValue(room.hotelRooms[0]?.typ);

const getSecondaryRoomKey = (room: RoomClass) => {
  const cat = getRoomValue(room.hotelRooms[0]?.cat);
  const typ = getTypeKey(room);

  return [cat, typ].filter(Boolean).join('|') || getRoomDisplayKey(room);
};

const toSortedGroupedRooms = (roomGroups: RoomClass[][]): GroupedRoomClass[] =>
  roomGroups.map(toGroupedRoom).sort((a, b) => a.price.usd - b.price.usd);

const getRoomsToPaintByOccupancy = (roomsToPaint: number, occupancy: number) => {
  if (occupancy >= 4) return 1;
  if (occupancy >= 2) return roomsToPaint >= 3 ? 2 : 1;
  return roomsToPaint;
};

const getHotelRoomDisplayedName = (hotelRoom: HotelRoom | undefined, isRoomNameCleared: boolean) => {
  if (!hotelRoom) return '';
  return isRoomNameCleared
    ? hotelRoom.displayNameCleared || hotelRoom.displayName || ''
    : hotelRoom.displayName || '';
};

const groupRoomsForCard = (
  rooms: RoomClass[],
  roomsToPaint: number,
  byCategoryMode = false
): GroupedRoomClass[] => {
  const groupingLevels: ((room: RoomClass) => string)[][] = byCategoryMode
    ? [[getCategoryKey], [getCategoryKey, getTypeKey]]
    : [[getBoardKey], [getBoardKey, getSecondaryRoomKey]];

  for (let levelIndex = 0; levelIndex < groupingLevels.length; levelIndex++) {
    const groupedRooms = toSortedGroupedRooms(groupRoomListsByKeys(rooms, groupingLevels[levelIndex]));

    if (groupedRooms.length >= roomsToPaint || levelIndex === groupingLevels.length - 1) {
      let result = groupedRooms.slice(0, roomsToPaint);

      if (!byCategoryMode && levelIndex === groupingLevels.length - 1) {
        const hasMeal = (room: GroupedRoomClass) =>
          (room.board?.basis?.code || 'RO') !== 'RO';

        if (
          result.length === roomsToPaint &&
          !result.some(hasMeal) &&
          groupedRooms.some(hasMeal)
        ) {
          const cheapestMeal = groupedRooms.find(hasMeal);
          if (cheapestMeal) {
            result = [...result.slice(0, roomsToPaint - 1), cheapestMeal].sort(
              (a, b) => a.price.usd - b.price.usd
            );
          }
        }
      }

      return result;
    }
  }

  return [];
};

const getCheapestOffer = (
  meta: RoomVariantMeta | undefined,
  type: 'red' | 'green',
  roomsByKey: Map<string, RoomClass>
): CheapestOffer | undefined => {
  if (!meta) return undefined;
  const fullRoom = roomsByKey.get(meta.rcUniqueKey);
  if (!fullRoom) return undefined;
  return { type, meta, room: fullRoom };
};

const getCheapestOfferBetween = (
  greenOffer: CheapestOffer | undefined,
  redOffer: CheapestOffer | undefined
): CheapestOffer | undefined => {
  if (!greenOffer) return redOffer;
  if (!redOffer) return greenOffer;
  return greenOffer.meta.price.usd <= redOffer.meta.price.usd ? greenOffer : redOffer;
};

const HotelCardRooms2: FC<HotelCardRooms2Props> = ({
  rooms,
  hotel,
  goToDetails,
  showRoomsInTab,
  onOpenRooms,
  roomsToPaint = 2,
  byCategoryMode = false,
  isSingle = false,
}) => {
  const { currency, priceMode } = useContext(MapHotelContext);
  const { user, userSiteConfig } = useContext(MainContext);
  const { toggleRoomProposal, isRoomInProposal } = useHotelProposal();
  const [cancellationModalVisibleIndex, setCancellationModalVisibleIndex] = useState('');
  const [remarkModalVisibleIndex, setRemarkModalVisibleIndex] = useState('');
  const filterRoom = new FilterRoom();

  const visibleRooms = rooms.filter((room) => room.visible);
  const occupancy = visibleRooms[0]?.hotelRooms.length || 1;
  const groupedRooms = groupRoomsForCard(
    visibleRooms,
    getRoomsToPaintByOccupancy(roomsToPaint, occupancy),
    byCategoryMode
  );
  const roomsByKey = new Map(visibleRooms.map((room) => [room.rcUniqueKey, room]));

  const getSuppText = (room: GroupedRoomClass) => {
    if (!user?.branch?.hotelDisplaySupplierName) return '';
    if (room.suppliers.length) return room.suppliers.join(', ');
    const { item } = hotel;
    return (item.sysSupp ? item.sysSupp : '') + ' ' + (item.sysSuppCode ? item.sysSuppCode : '');
  };

  const onClickPrice = (
    offer: CheapestOffer | undefined,
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    if (!offer) return;

    if (showRoomsInTab && onOpenRooms) {
      onOpenRooms(offer.room.rcUniqueKey, e);
    } else {
      goToDetails({ hotel, room: offer.room, filterRoom });
    }
  };

  const renderPrice = (offer: CheapestOffer | undefined, className = '') => {
    if (!offer) {
      return <div className="hotel-card-room-v4-price" />;
    }

    const mainPriceHtml = formatPriceHotel(offer.meta.price, currency, priceMode);
    const altPriceText = formatPriceHotel(offer.meta.price, offer.meta.price.currency);
    const priceTooltip = mainPriceHtml.replace(/<[^>]*>/g, '');

    return (
      <div
        className={`hotel-card-room-v4-price price-hover-target cur-poi ${className}`}
        onClick={(e) => onClickPrice(offer, e)}
      >
        <Tooltip content={priceTooltip} position="top">
          <span className="price-hover-swap">
            <span className="price-hover-values text-bold">
              <span
                className="hotel-card-room-v4-price-main"
                dangerouslySetInnerHTML={{
                  __html: mainPriceHtml,
                }}
              />
              {user?.branch?.hotelDisplaySupplierName && (
                <span className="text-light text-normal hotel-card-room-v4-price-alt">
                  {altPriceText}
                </span>
              )}
            </span>
            <span className="price-hover-arrow" aria-hidden>
              <FontAwesomeIcon icon={faCircleArrowRight} />
            </span>
          </span>
        </Tooltip>
      </div>
    );
  };

  const activeOfferRoom =
    roomsByKey.get(cancellationModalVisibleIndex) || roomsByKey.get(remarkModalVisibleIndex);

  return (
    <div className={`w100p hotel-card-rooms-intab hotel-card-rooms-count-${occupancy}`}>
      <div className="hotel-card-rooms-ungroup">
        <div className="hotel-card-rooms">
          {groupedRooms.map((room, index) => {
            const greenOffer = getCheapestOffer(room.greenPrices[0], 'green', roomsByKey);
            const redOffer = getCheapestOffer(room.redData[0], 'red', roomsByKey);
            const singleOffer = isSingle ? getCheapestOfferBetween(greenOffer, redOffer) : undefined;
            const iconsOffer = singleOffer || greenOffer || redOffer;
            const isHighlighted = isSingle
              ? hotel.highlightedUk === singleOffer?.room.rcUniqueKey
              : hotel.highlightedUk === greenOffer?.room.rcUniqueKey ||
              hotel.highlightedUk === redOffer?.room.rcUniqueKey;

            return (
              <div
                className={
                  'hotel-card-room' +
                  (isHighlighted ? ' hotel-card-room-highlighted' : '') +
                  (isSingle ? ' hotel-card-room-v4-single' : '') +
                  (room.hotelRooms.length > 1 ? ' hotel-card-room-auto' : '')
                }
                key={`${room.board?.basis?.code || ''}-${room.rcUniqueKey || index}`}
              >
                <div className="flex-one hotel-card-room-v4-name">
                  <div className="text-bold text-dark">
                    {room.hotelRooms.map((hotelRoom, hotelRoomIndex) => {
                      const displayedName = getHotelRoomDisplayedName(
                        hotelRoom,
                        userSiteConfig.hotel.isRoomNameCleared
                      );
                      const roomName = displayedName.replace(/<[^>]*>/g, '').trim();

                      return (
                        <div key={hotelRoomIndex} className="hotel-card-room-v4-name-line text-lh-sm mb-0">
                          <Tooltip content={roomName} position="top">
                            <span
                              dangerouslySetInnerHTML={{ __html: displayedName || '' }}
                            />
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-light text-normal flex-row">
                    {iconsOffer && (
                      <ProposalHeart
                        active={!!isRoomInProposal(hotel, iconsOffer.room)}
                        onClickPropsal={(e) => {
                          e.stopPropagation();
                          toggleRoomProposal(hotel, iconsOffer.room);
                        }}
                        isEnvelope={true}
                      />
                    )}
                    <div className='text-limited' style={{ maxWidth: 'calc(100% - 25px)' }}>
                      {getSuppText(room)}

                    </div>
                  </div>
                </div>

                {isSingle ? (
                  <>
                    <div className="hotel-card-room-v4-icons text-normal flex-row">
                      {iconsOffer && (
                        <RoomIcons
                          boardCode={iconsOffer.meta.board.basis.code}
                          limitDate={iconsOffer.meta.cxl.limitDate}
                          ada={!!iconsOffer.room.hotelRooms[0]?.ada}
                          hideRemarks={true}
                          onCancellationClick={() =>
                            user?.branch?.hotelDisplaySupplierName &&
                            setCancellationModalVisibleIndex(iconsOffer.meta.rcUniqueKey)
                          }
                          onRemarksClick={() =>
                            setRemarkModalVisibleIndex(iconsOffer.meta.rcUniqueKey)
                          }
                        />
                      )}
                    </div>

                    {renderPrice(
                      singleOffer,
                      singleOffer?.type === 'red' ? 'color-danger' : ''
                    )}
                  </>
                ) : (
                  <>
                    {renderPrice(greenOffer)}

                    <div className="hotel-card-room-v4-icons text-normal flex-row">
                      {iconsOffer && (
                        <RoomIcons
                          boardCode={iconsOffer.meta.board.basis.code}
                          limitDate={iconsOffer.meta.cxl.limitDate}
                          ada={!!iconsOffer.room.hotelRooms[0]?.ada}
                          hideRemarks={true}
                          onCancellationClick={() =>
                            user?.branch?.hotelDisplaySupplierName &&
                            setCancellationModalVisibleIndex(iconsOffer.meta.rcUniqueKey)
                          }
                          onRemarksClick={() =>
                            setRemarkModalVisibleIndex(iconsOffer.meta.rcUniqueKey)
                          }
                        />
                      )}
                    </div>

                    {renderPrice(redOffer, 'color-danger')}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {!!cancellationModalVisibleIndex && activeOfferRoom && (
        <CancellationPolicyModal
          hotel={hotel}
          onClose={() => setCancellationModalVisibleIndex('')}
          rc={activeOfferRoom}
        />
      )}

      {!!remarkModalVisibleIndex && activeOfferRoom && (
        <CancellationPolicyModal
          hotel={hotel}
          onClose={() => setRemarkModalVisibleIndex('')}
          rc={activeOfferRoom}
          isRemark={true}
        />
      )}
    </div>
  );
};

export default HotelCardRooms2;
